import type { ShareExpiry } from '@see-stella/types'
import { useCallback, useState } from 'react'
import { authedRegenerate, ShareApiError } from '../../lib/shareApi'
import { logError } from '../../lib/logger'

/** The three sections the owner can toggle (Lifestyle toggle lands in a later slice). */
export type Includes = { photos: boolean; metrics: boolean; routine: boolean }

export interface OwnerShare {
  includes: Includes
  /** Toggle one section. Photos/Metrics are independent; the last-on section can't be turned off. */
  setInclude(key: keyof Includes, on: boolean): void
  /** True while a share link is being minted. */
  creating: boolean
  /** A user-facing error from the last create attempt, or null. */
  error: string | null
  /**
   * Mint a link-only share with the current sections + expiry, then open the OS
   * share sheet (best-effort). Resolves `true` when the link was minted, `false`
   * when the mint failed (in which case `error` is set) — never rejects, so the
   * caller can decide whether to close the sheet without an unhandled rejection.
   */
  create(expiry: ShareExpiry): Promise<boolean>
}

/**
 * Hand the URL to the OS share sheet, falling back to the clipboard when Web Share
 * is unavailable OR fails (e.g. an embedded WebView without Web Share, or the
 * transient-activation window lost after the mint's network round-trip). A user
 * cancelling the sheet (AbortError) counts as shared, not a silent copy. Best-effort:
 * never throws, so a share/copy hiccup can't fail an already-minted link. The robust
 * in-app path (a postMessage bridge to native Share) lands with Manage (plan 5.4).
 */
async function shareOrCopy(url: string): Promise<void> {
  if (typeof navigator === 'undefined') return
  if (typeof navigator.share === 'function') {
    try {
      await navigator.share({ url })
      return
    } catch (e) {
      // AbortError = the user cancelled the sheet -> treat as shared, not a copy.
      // Read .name off DOMException too (it may not chain off Error in older or
      // embedded WebView engines).
      const name =
        e instanceof DOMException || e instanceof Error ? e.name : ''
      if (name === 'AbortError') return
      // otherwise fall through to the clipboard (unsupported target / lost activation)
    }
  }
  try {
    await navigator.clipboard?.writeText(url)
  } catch (e) {
    logError('share: clipboard copy failed', e)
  }
}

/**
 * Owner share state for the control bar + Create step. Holds which sections are
 * shared and mints a link-only share on `create`. The Firebase token is requested
 * per call via the injected `requestToken` (the native handshake), so each mint
 * uses a current token. Manage/write-through (update/revoke, hydrate from getMine)
 * lands in a later slice.
 */
export function useOwnerShare(
  requestToken: () => Promise<string | null>
): OwnerShare {
  const [includes, setIncludes] = useState<Includes>({
    photos: true,
    metrics: true,
    routine: false,
  })
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const setInclude = useCallback((key: keyof Includes, on: boolean) => {
    setIncludes((prev) => {
      const next = { ...prev, [key]: on }
      // At least one section must stay shared — block the last one turning off.
      if (!next.photos && !next.metrics && !next.routine) return prev
      return next
    })
  }, [])

  const create = useCallback(
    async (expiry: ShareExpiry): Promise<boolean> => {
      setCreating(true)
      setError(null)
      try {
        const token = await requestToken()
        if (!token) throw new ShareApiError('unknown')
        const { url } = await authedRegenerate(token, {
          includePhotos: includes.photos,
          includeMetrics: includes.metrics,
          includeRoutine: includes.routine,
          includeLifestyle: false,
          expiry,
        })
        await shareOrCopy(url)
        return true
      } catch (e) {
        logError('share: create failed', e)
        setError('Could not create your share link. Please try again.')
        return false
      } finally {
        setCreating(false)
      }
    },
    [requestToken, includes]
  )

  return { includes, setInclude, creating, error, create }
}
